import LockIcon from "@mui/icons-material/Lock";
import SaveCancelButtons from "designSystem/components/button/SaveCancelButtons";
import { Box, FlexColumn, FlexRow, FlexRowCenter, GridColumn, LoadingPlaceholder, MultiSelect, SingleSelect, Switch, TextField } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { observer } from "mobx-react-lite";
import React, { ReactNode } from "react";
import { Channel, Panel, Team, User, UserProvider } from "../api";
import ChatFormField from "./ChatFormField";
import { ChatStore } from "./ChatStore";
import { useChannelNameStyles } from "./styles";

interface AddUserProps<Store extends ChatStore<Team, User, Channel, Error, UserProvider<User>, Panel>> extends Pick<ChannelFormProps<Store>, "store"> { }

const AddUser = observer((<Store extends ChatStore<Team, User, Channel, Error, UserProvider<User>, Panel>>(props: AddUserProps<Store>) => {
	const { store } = props;
	const loc = useLocale();

	const {
		selectedUsers,
		setSelectedUsers,
		shouldDisableAddPeople,
		userDictionary,
	} = store;

	return (
		<ChatFormField label={loc.resource("AddPeopleLabel")}>
			<MultiSelect
				disabled={shouldDisableAddPeople}
				loading={!userDictionary}
				onChange={setSelectedUsers}
				options={userDictionary}
				value={selectedUsers}
			/>
		</ChatFormField>
	);
}));

export interface ChannelFormProps<Store extends ChatStore<Team, User, Channel, Error, UserProvider<User>, Panel>> {
	/** custom component to populate the name field */
	NameComponent?: ReactNode;

	/** indicate if all data is ready to use */
	dataReady: boolean;

	/** store to serve this component */
	store: Store;
};

const ChannelForm = observer(<Store extends ChatStore<Team, User, Channel, Error, UserProvider<User>, Panel>>(props: ChannelFormProps<Store>) => {
	const loc = useLocale();
	const { dataReady, store, NameComponent } = props;
	const { nameIcon, nameInput } = useChannelNameStyles();
	const {
		channelDescriptionErrorResource,
		channelNameErrorResource,
		channelIsPrivate,
		handleOnBack,
		handleOnSaveNewChannel,
		loadingSubtitleRes,
		newChannelDisplayName,
		showAddPeopleConditionally,
		setChannelIsPrivate,
		setNewChannelDescription,
		setNewChannelName,
		setTeam,
		teamIdErrorResource,
		teamFieldLabelRes,
		teams,
	} = store;

	return (
		<FlexRow flexGrow={1} justifyContent="center">
			{dataReady ? (
				<FlexColumn flexGrow={1}>
					<FlexRow flexGrow={1} height={1}>
						<GridColumn fullHeight scroll>
							{teams && (
								<ChatFormField error={teamIdErrorResource ? loc.resource(teamIdErrorResource) : null} label={loc.resource(teamFieldLabelRes)}>
									<SingleSelect
										onChange={setTeam}
										options={teams}
										required
									/>
								</ChatFormField>
							)}
							{NameComponent ?? (
								<ChatFormField error={channelNameErrorResource ? loc.resource(channelNameErrorResource) : null} label={loc.resource("ChannelNameLabel")}>
									<Box position="relative">
										<TextField
											InputProps={{ sx: channelIsPrivate ? nameInput : {} }}
											fullWidth
											onChange={setNewChannelName}
											value={newChannelDisplayName}
										/>
										{channelIsPrivate &&
											<LockIcon sx={nameIcon} />
										}
									</Box>
								</ChatFormField>
							)}
							<ChatFormField error={channelDescriptionErrorResource ? loc.resource(channelDescriptionErrorResource) : null} label={loc.resource("Description")}>
								<TextField
									fullWidth
									multiline
									onChange={setNewChannelDescription}
									rows={3}
								/>
							</ChatFormField>
							{showAddPeopleConditionally &&
								<AddUser store={store} />
							}
							<ChatFormField info={loc.resource("MakeChannelPrivateInfo")} label={loc.resource("MakeChannelPrivateLabel")}>
								<Switch checked={channelIsPrivate} onChange={setChannelIsPrivate} />
							</ChatFormField>
						</GridColumn>
					</FlexRow>
					<FlexRowCenter
						borderTop={STANDARD_BORDER_STYLE}
						px={3}
						py={2}
					>
						<SaveCancelButtons
							onCancel={handleOnBack}
							onSave={handleOnSaveNewChannel}
						/>
					</FlexRowCenter>
				</FlexColumn>
			) : (
				<LoadingPlaceholder
					subtitleProps={{ res: loadingSubtitleRes }}
					titleProps={{ res: "Loading" }}
				/>
			)}
		</FlexRow>
	);
});

export default ChannelForm;